import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import LoyaltyPanel from './LoyaltyPanel';
import { useTranslation } from 'react-i18next';

const WalletDashboard = () => {
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('balance');
  const [transferForm, setTransferForm] = useState({
    recipient_phone: '',
    amount: '',
    message: ''
  });
  const [giftCardForm, setGiftCardForm] = useState({
    amount: '',
    recipient_phone: '',
    recipient_email: '',
    message: ''
  });
  const [topUpAmount, setTopUpAmount] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const { sessionToken, user } = useUnifiedAuth();
  const { t } = useTranslation();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/balance`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWalletBalance(parseFloat(data.balance));
        }
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/transactions`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTransactions(data.transactions || []);
        }
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleTopUp = async () => {
    if (!topUpAmount || parseFloat(topUpAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/top-up`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(topUpAmount),
          payment_method: 'myfatoorah',
          success_url: `${window.location.origin}/wallet?payment=success`,
          cancel_url: `${window.location.origin}/wallet?payment=cancelled`
        })
      });

      const data = await response.json();
      if (data.success && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.error || 'Failed to create payment session');
      }
    } catch (error) {
      console.error('Error creating top-up:', error);
      alert('Error processing top-up request');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!transferForm.recipient_phone || !transferForm.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferForm)
      });

      const data = await response.json();
      if (data.success) {
        alert(`Successfully transferred ${transferForm.amount} KWD to ${data.recipient_name}`);
        setTransferForm({ recipient_phone: '', amount: '', message: '' });
        fetchWalletBalance();
        fetchTransactions();
      } else {
        alert(data.error || 'Transfer failed');
      }
    } catch (error) {
      console.error('Error transferring money:', error);
      alert('Error processing transfer');
    }
  };

  const handleCreateGiftCard = async (e) => {
    e.preventDefault();

    if (!giftCardForm.amount || !giftCardForm.recipient_phone) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/gift-card`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...giftCardForm,
          payment_method: 'wallet'
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Gift card created successfully! Code: ${data.gift_card_code}`);
        setGiftCardForm({ amount: '', recipient_phone: '', recipient_email: '', message: '' });
        fetchWalletBalance();
        fetchTransactions();
      } else {
        alert(data.error || 'Failed to create gift card');
      }
    } catch (error) {
      console.error('Error creating gift card:', error);
      alert('Error creating gift card');
    }
  };

  const handleRedeemGiftCard = async () => {
    if (!redeemCode.trim()) {
      alert('Please enter a gift card code');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/redeem-gift-card`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gift_card_code: redeemCode })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Successfully redeemed ${data.amount} KWD! New balance: ${data.new_balance} KWD`);
        setRedeemCode('');
        fetchWalletBalance();
        fetchTransactions();
      } else {
        alert(data.error || 'Failed to redeem gift card');
      }
    } catch (error) {
      console.error('Error redeeming gift card:', error);
      alert('Error redeeming gift card');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + 
           new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'top_up': return '💰';
      case 'transfer_send': return '📤';
      case 'transfer_receive': return '📥';
      case 'gift_card_purchase': return '🎁';
      case 'gift_card_redeem': return '🎫';
      case 'payment': return '💳';
      case 'refund': return '↩️';
      default: return '💸';
    }
  };

  useEffect(() => {
    if (sessionToken) {
      fetchWalletBalance();
      fetchTransactions();
    }
  }, [sessionToken]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
          <div className="h-32 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('payment.wallet.title', 'My Wallet')}</h1>
        <p className="text-gray-600">{t('payment.wallet.subtitle', 'Manage your balance and your points')}</p>
      </div>

      {/* Wallet Balance Card */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium opacity-90">{t('payment.wallet.balance', 'Balance')}</h2>
            <p className="text-3xl font-bold"><bdi>{walletBalance.toFixed(2)} {t('common.currency', 'KWD')}</bdi></p>
            <p className="numeric-ltr text-sm opacity-75 mt-1">Maximum: 750 KWD</p>
          </div>
          <div className="text-4xl">💳</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1">
        {[
          { key: 'balance', label: t('payment.wallet.topUp', 'Top Up'), icon: '💰' },
          { key: 'points', label: t('loyalty.tab', 'Points'), icon: '🎁' },
          // PAUSED: Transfer and Gift Cards features
          // { key: 'transfer', label: 'Transfer', icon: '💸' },
          // { key: 'giftcard', label: 'Gift Cards', icon: '🎁' },
          { key: 'transactions', label: t('payment.wallet.history', 'History'), icon: '📋' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        {activeTab === 'points' && <LoyaltyPanel sessionToken={sessionToken} />}
        {activeTab === 'balance' && (
          <div>
            <h3 className="text-xl font-semibold mb-4">Top Up Wallet</h3>
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (KWD)
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max="750"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleTopUp}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Top Up
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Minimum: 1 KWD • Maximum: 750 KWD per transaction
              </p>
            </div>

            {/* PAUSED: Gift Card Redemption
            <div className="mt-8">
              <h4 className="text-lg font-medium mb-4">Redeem Gift Card</h4>
              <div className="max-w-md">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value)}
                    placeholder="Enter gift card code"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleRedeemGiftCard}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Redeem
                  </button>
                </div>
              </div>
            </div>
            */}
          </div>
        )}

        {activeTab === 'transfer' && (
          <div>
            <h3 className="text-xl font-semibold mb-4">Transfer Money</h3>
            <form onSubmit={handleTransfer} className="max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Phone Number *
                </label>
                <input
                  type="tel"
                  value={transferForm.recipient_phone}
                  onChange={(e) => setTransferForm({...transferForm, recipient_phone: e.target.value})}
                  placeholder="+965 XXXXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (KWD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max="750"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <input
                  type="text"
                  value={transferForm.message}
                  onChange={(e) => setTransferForm({...transferForm, message: e.target.value})}
                  placeholder="Add a message"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Send Money
              </button>
            </form>
          </div>
        )}

        {activeTab === 'giftcard' && (
          <div>
            <h3 className="text-xl font-semibold mb-4">Create Gift Card</h3>
            <form onSubmit={handleCreateGiftCard} className="max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (KWD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max="750"
                  value={giftCardForm.amount}
                  onChange={(e) => setGiftCardForm({...giftCardForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Phone Number *
                </label>
                <input
                  type="tel"
                  value={giftCardForm.recipient_phone}
                  onChange={(e) => setGiftCardForm({...giftCardForm, recipient_phone: e.target.value})}
                  placeholder="+965 XXXXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email (Optional)
                </label>
                <input
                  type="email"
                  value={giftCardForm.recipient_email}
                  onChange={(e) => setGiftCardForm({...giftCardForm, recipient_email: e.target.value})}
                  placeholder="recipient@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personal Message (Optional)
                </label>
                <textarea
                  value={giftCardForm.message}
                  onChange={(e) => setGiftCardForm({...giftCardForm, message: e.target.value})}
                  placeholder="Add a personal message"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Create Gift Card
              </button>
            </form>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div>
            <h3 className="text-xl font-semibold mb-4">Transaction History</h3>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📋</div>
                <p className="text-gray-600">No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">{getTransactionIcon(tx.transaction_type)}</div>
                      <div>
                        <p className="font-medium text-gray-900">{tx.description}</p>
                        <p className="text-sm text-gray-600">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        tx.transaction_type.includes('receive') || tx.transaction_type === 'top_up' || tx.transaction_type === 'gift_card_redeem'
                          ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.transaction_type.includes('receive') || tx.transaction_type === 'top_up' || tx.transaction_type === 'gift_card_redeem' ? '+' : '-'}
                        {tx.amount} KWD
                      </p>
                      <p className={`text-sm px-2 py-1 rounded-full ${
                        tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                        tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {tx.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletDashboard;